import { db } from '../lib/firebase';
import { 
  doc, 
  updateDoc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../lib/firebase';

export interface ContentUpdateData {
  title?: string;
  description?: string;
  category?: string;
  visibility?: string;
  genre?: string;
  genres?: string[];
  tags?: string;
  contentRating?: string;
  rating?: string;
  language?: string;
  languages?: string[];
  year?: number;
  duration?: number;
  
  // Book-specific fields
  author?: string;
  
  // Movie-specific fields
  cast?: string;
  director?: string;
  writer?: string;
  contentAdvisory?: string[];
  trailerFile?: File;
  
  // Series-specific fields
  episodes?: any[];
  seasonNumber?: string;
  episodeNumber?: string;
  
  // Comic-specific fields
  pages?: any[];
  chapterNumber?: string;
  
  // Novel-specific fields
  chapters?: any[];
  
  // File updates
  thumbnailFile?: File;
  posterFile?: File;
  backdropFile?: File;
  videoFile?: File;
  audioFile?: File;
  subtitleFile?: File;
  bookFile?: File;
  coverFile?: File;
}

export interface ContentUpdateResult {
  success: boolean;
  message: string;
  updatedFields: string[];
  newFileUrls: { [key: string]: string };
}

/**
 * Update content metadata and files
 */
export async function updateContent(
  contentId: string,
  userId: string,
  updateData: ContentUpdateData
): Promise<ContentUpdateResult> {
  try {
    // Verify user owns the content
    const contentRef = doc(db, 'content', contentId);
    const contentSnap = await getDoc(contentRef);
    
    if (!contentSnap.exists()) {
      return {
        success: false,
        message: 'Content not found',
        updatedFields: [],
        newFileUrls: {}
      };
    }

    const contentData = contentSnap.data();
    if (contentData.uploaderId !== userId) {
      return {
        success: false,
        message: 'You do not have permission to update this content',
        updatedFields: [],
        newFileUrls: {}
      };
    }

    const updatedFields: string[] = [];
    const newFileUrls: { [key: string]: string } = {};
    const updatePayload: any = {
      updatedAt: serverTimestamp()
    };

    // Import sanitization utilities
    const { sanitizeContentMetadata, sanitizeText, sanitizeStringArray } = await import('../utils/inputSanitizer');
    
    // Update metadata fields with sanitization
    const metadataFields = [
      'title', 'description', 'category', 'visibility', 'genre', 'genres', 'tags', 
      'contentRating', 'rating', 'language', 'languages', 'year', 'duration',
      'author', 'cast', 'director', 'writer', 'contentAdvisory', 'episodes', 'pages', 'chapters',
      'seasonNumber', 'episodeNumber', 'chapterNumber'
    ];
    metadataFields.forEach(field => {
      if (updateData[field as keyof ContentUpdateData] !== undefined) {
        const value = updateData[field as keyof ContentUpdateData];
        
        // Sanitize text fields
        if (typeof value === 'string') {
          if (field === 'title') {
            updatePayload[field] = sanitizeText(value, 200);
          } else if (field === 'description') {
            updatePayload[field] = sanitizeText(value, 5000);
          } else if (['cast', 'director', 'writer', 'contentAdvisory'].includes(field)) {
            updatePayload[field] = sanitizeStringArray(value, 50);
          } else if (field === 'tags') {
            updatePayload[field] = sanitizeStringArray(value, 20);
          } else {
            updatePayload[field] = sanitizeText(value, 100);
          }
        } else if (Array.isArray(value)) {
          // Sanitize array fields
          if (['cast', 'director', 'writer', 'contentAdvisory', 'tags', 'genres'].includes(field)) {
            updatePayload[field] = sanitizeStringArray(value, 50);
          } else {
            updatePayload[field] = value; // Keep arrays like episodes as-is (they're complex objects)
          }
        } else {
          // Keep non-string values as-is (numbers, booleans, objects)
          updatePayload[field] = value;
        }
        
        updatedFields.push(field);
      }
    });

    // Handle file uploads
    const fileFields = [
      { key: 'thumbnailFile', storagePath: 'thumbnails', fieldName: 'thumbnail' },
      { key: 'posterFile', storagePath: 'posters', fieldName: 'poster' },
      { key: 'backdropFile', storagePath: 'backdrops', fieldName: 'backdrop' },
      { key: 'videoFile', storagePath: 'videos', fieldName: 'video', isAsset: true },
      { key: 'trailerFile', storagePath: 'trailers', fieldName: 'trailer' },
      { key: 'audioFile', storagePath: 'audio', fieldName: 'audioUrl' },
      { key: 'subtitleFile', storagePath: 'subtitles', fieldName: 'subtitleUrl' },
      { key: 'bookFile', storagePath: 'books', fieldName: 'bookFile', isAsset: true },
      { key: 'coverFile', storagePath: 'book-covers', fieldName: 'coverFile', isAsset: true }
    ];

    for (const fileField of fileFields) {
      const file = updateData[fileField.key as keyof ContentUpdateData] as File;
      if (file) {
        try {
          // Delete old file if it exists
          let oldUrl;
          if (fileField.isAsset) {
            oldUrl = contentData.assets?.[fileField.fieldName];
          } else {
            oldUrl = contentData[fileField.fieldName];
          }
          
          if (oldUrl && oldUrl.includes('firebasestorage')) {
            try {
              const oldRef = ref(storage, oldUrl);
              await deleteObject(oldRef);
            } catch (error) {
              // Silently fail - old file deletion is not critical
            }
          }

          // Upload new file
          const fileName = `${Date.now()}_${file.name}`;
          const fileRef = ref(storage, `${fileField.storagePath}/${userId}/${fileName}`);
          await uploadBytes(fileRef, file);
          const downloadUrl = await getDownloadURL(fileRef);
          
          // Handle assets structure for video files
          if (fileField.isAsset) {
            // Initialize assets object if it doesn't exist
            if (!updatePayload.assets) {
              updatePayload.assets = contentData.assets || {};
            }
            updatePayload.assets[fileField.fieldName] = downloadUrl;
            newFileUrls[fileField.fieldName] = downloadUrl;
          } else {
            updatePayload[fileField.fieldName] = downloadUrl;
            newFileUrls[fileField.fieldName] = downloadUrl;
          }
          updatedFields.push(fileField.fieldName);
        } catch (error) {
          console.error(`Error uploading ${fileField.key}:`, error);
          return {
            success: false,
            message: `Failed to upload ${fileField.key}`,
            updatedFields,
            newFileUrls
          };
        }
      }
    }

    // Update the document
    await updateDoc(contentRef, updatePayload);

    return {
      success: true,
      message: 'Content updated successfully',
      updatedFields,
      newFileUrls
    };

  } catch (error) {
    console.error('Error updating content:', error);
    return {
      success: false,
      message: 'Failed to update content. Please try again.',
      updatedFields: [],
      newFileUrls: {}
    };
  }
}

/**
 * Get content for editing
 */
export async function getContentForEdit(contentId: string, userId: string) {
  try {
    const contentRef = doc(db, 'content', contentId);
    const contentSnap = await getDoc(contentRef);
    
    if (!contentSnap.exists()) {
      throw new Error('Content not found');
    }

    const contentData = contentSnap.data();
    if (contentData.uploaderId !== userId) {
      throw new Error('You do not have permission to edit this content');
    }

    const result = { 
      id: contentSnap.id, 
      title: contentData.title || '',
      description: contentData.description || '',
      category: contentData.category || 'movie',
      visibility: contentData.visibility || 'public',
      genre: contentData.genre || '',
      genres: contentData.genres || [],
      tags: contentData.tags || [],
      contentRating: contentData.contentRating || 'G',
      language: contentData.language || 'English',
      languages: contentData.languages || [],
      year: contentData.year || new Date().getFullYear(),
      duration: contentData.duration || 0,
      // Movie-specific fields
      cast: contentData.cast || [],
      director: contentData.director || [],
      writer: contentData.writer || [],
      contentAdvisory: contentData.contentAdvisory || [],
      // Media fields
      poster: contentData.poster || '',
      thumbnail: contentData.thumbnail || '',
      trailer: contentData.trailer || '',
      video: contentData.video || '',
      assets: contentData.assets || {},
      // Series-specific fields
      episodes: contentData.episodes || [],
      // Comic-specific fields
      pages: contentData.pages || [],
      // Novel-specific fields
      chapters: contentData.chapters || [],
      ...contentData 
    };
    
    return result;
  } catch (error) {
    console.error('Error getting content for edit:', error);
    throw error;
  }
}
